import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { founder, salon } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { kuurLabel, treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { FormError } from '../shared/FormError';
import { longDate, shortDate, today, weekdayLong } from '../../core/format/dates';
import { isKuurExpired } from '../../core/rules/kuur';
import { ease } from '../../core/format/motion';
import { shopLabels } from '../../data/shopLabels';
import { chipCls, choiceState } from '../../ui/styles';
import { acquisitionQuestion } from '../../data/acquisition';
import { SourceChips } from '../shared/SourceChips';
import { PerformerChoice } from './PerformerChoice';
import type { BookingControl } from './useBooking';
export const BOOKING_FORM_ID = 'booking-form';
const DETAILS_ID = 'booking-details';

/** Tijden ("09:30") per dagdeel, in volgorde; lege dagdelen vallen weg. */
function dayParts(slots: string[]): {
  label: string;
  slots: string[];
}[] {
  const hour = (t: string) => Number(t.slice(0, 2));
  return [{
    label: 'Ochtend',
    slots: slots.filter(t => hour(t) < 12)
  }, {
    label: 'Middag',
    slots: slots.filter(t => hour(t) >= 12 && hour(t) < 17)
  }, {
    label: 'Avond',
    slots: slots.filter(t => hour(t) >= 17)
  }].filter(p => p.slots.length > 0);
}
const legend = 'font-display text-lg text-ink';
const chip = chipCls;
// 16px op de telefoon: kleiner laat de iPhone inzoomen bij het tikken.
const input = 'w-full rounded-sm border border-line bg-sand px-4 py-3 text-[16px] text-ink placeholder:text-muted transition-colors duration-150 ease-soft focus:border-ink focus:outline-none sm:text-[15px]';
const linkCls = 'text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay';
const visitOptions = [{
  value: true,
  label: 'Eerste keer'
}, {
  value: false,
  label: 'Ik ben al klant'
}];

/** De aanvraag zelf: wie, welke behandeling, welke dag en tijd. */
export function BookingForm({
  b
}: {
  b: BookingControl;
}) {
  const {
    policy
  } = useSite();
  const reduce = useReducedMotion();
  const {
    account,
    selected,
    session,
    course
  } = b;

  // Tijd gekozen en nog geen gegevens: de volgende stap in beeld schuiven (op de telefoon staat die
  // onder de vouw). Geen focus verplaatsen: dan springt het toetsenbord ongevraagd open.
  const pickTime = (t: string) => {
    b.setTime(t);
    if (account || b.name) return;
    document.getElementById(DETAILS_ID)?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  return <form id={BOOKING_FORM_ID} onSubmit={b.submit} aria-busy={b.submitting} className="space-y-9" noValidate data-id="element-8380" data-mp="17h46cf8h4f4f">
      <FormError message={b.error} data-id="element-8381" data-mp="1cggssyvfnk6h" />

      {account ? <div className="bg-sand px-4 py-3.5 text-[14px] leading-relaxed" data-id="element-8382" data-mp="1cdukgx0px0n7">
          <p className="text-ink" data-id="element-8383" data-mp="2iv81vpupmbbl">
            Je boekt als {account.customer.firstName} {account.customer.lastName}
          </p>
          <p className="text-muted" data-id="element-8384" data-mp="2nwxc53a3ykym">
            {account.customer.email}
            {b.knownFirstVisit && ' · je eerste bezoek begint met een uitgebreide intake'}
          </p>
        </div> : <fieldset data-id="element-8385" data-mp="7b0flxyg5x64">
          <legend className={legend} data-id="element-8386" data-mp="2ho3uoev9kus">Ben je al eens geweest?</legend>
          <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-8387" data-mp="2futoz51oii1">
            {visitOptions.map(o => <button key={o.label} type="button" aria-pressed={b.firstVisit === o.value} onClick={() => b.setFirstVisit(o.value)} className={chip(b.firstVisit === o.value)} data-id="element-8388" data-mp="3tm4mbcw2v5l7">
                {o.label}
              </button>)}
          </div>
          <AnimatePresence initial={false} data-id="element-8389" data-mp="3fno8z33pmgu3">
            {b.firstVisit && <motion.p initial={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} animate={reduce ? {
          opacity: 1
        } : {
          height: 'auto',
          opacity: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="overflow-hidden" data-id="element-8390" data-mp="3apmqaja74jj5">
                <span className="mt-3 block bg-sand px-4 py-3 text-[14px] leading-relaxed text-muted" data-id="element-8391" data-mp="6ir60se47cby">
                  Welkom! Je eerste behandeling start met een uitgebreide intake van je huid en routine. Deze reken je vooraf online af; daarna betaal je gewoon in de
                  salon.
                </span>
              </motion.p>}
          </AnimatePresence>
        </fieldset>}

      <fieldset data-id="element-8392" data-mp="3nsp758wzl303">
        <legend className={legend} data-id="element-8393" data-mp="3nt8qmu2abzg8">Behandeling</legend>
        <label htmlFor="booking-treatment" className="sr-only" data-id="element-8394" data-mp="3nu0chd62yitb">
          Kies een behandeling
        </label>
        <select id="booking-treatment" value={b.treatment} onChange={e => b.setTreatment(e.target.value)} aria-describedby={selected ? 'booking-treatment-note' : undefined} className={`mt-3 ${input}`} data-id="element-8395" data-mp="39z7rw0bc2cfv">
          {b.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-8396" data-mp="35425l0e0qvr9">
              {t.name} — {treatmentDuration(t)}, {treatmentPrice(t)}
            </option>)}
        </select>
        {selected && <p id="booking-treatment-note" className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-8397" data-mp="2hgx824lsm3ne">
            {selected.tagline}
            {selected.kuur && !session ? ` · kuur: ${kuurLabel(selected)}` : ''}
          </p>}
        {session && course && <p className="mt-2 text-[14px] text-clay" data-id="element-8398" data-mp="34dz0u0oc5rb2">
            Dit wordt sessie {session} van {course.course.total} van je kuur.
            {course.course.prepaid && (isKuurExpired(course.course, today()) ? ' Je vooruitbetaalde kuur is verlopen; deze sessie reken je in de salon af.' : ' Al betaald, je hoeft niets meer af te rekenen.')}
          </p>}
      </fieldset>

      <PerformerChoice b={b} data-id="element-8399" data-mp="3o2hnaahyiqm2" />

      <fieldset data-id="element-8400" data-mp="3o3sr2abejyd6">
        <legend className={legend} data-id="element-8401" data-mp="3o16efw9nmso3">Dag</legend>
        {b.slotsLoading ? <p className="mt-3 text-[14px] text-muted" role="status" data-id="element-8402" data-mp="3j5r6tm28pt04">
            Vrije tijden ophalen…
          </p> : b.days.length === 0 ? <p className="mt-3 text-[14px] text-muted" data-id="element-8403" data-mp="3v67k45zk5d3p">
            De komende weken is er geen plek.{' '}
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={linkCls} data-id="element-8404" data-mp="3zo1i4nhciuu">
              App {founder.firstName}
              <span className="sr-only" data-id="element-8405" data-mp="8ro8gh1ywc0x">{shopLabels.viaWhatsApp}</span>
            </a>{' '}
            voor een plekje tussendoor.
          </p> : <>
          {b.approximate && <p className="mt-3 text-[13px] text-muted" role="status" data-id="element-8406" data-mp="hpzvk93mkrom">
              De agenda laadde even niet. Je ziet de openingstijden; is een tijd toch bezet, dan hoor je dat meteen en kies je een andere.
            </p>}
          <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-8407" data-mp="rne9a0y5z2d8">
            {b.days.map((d, i) => {
            const active = b.dayIndex === i;
            const full = b.dayFull(i);
            const who = b.dayStaff(i);
            return <button key={d.toISOString()} type="button" aria-pressed={active} aria-label={`${longDate(d)}${who ? `, met ${who}` : ''}${full ? ', vol' : ''}`} disabled={full} onClick={() => b.pickDay(i)}
            // Vol: zichtbaar gezegd en gestippeld, niet alleen vager (dat was op zand slecht leesbaar).
            className={`rounded-sm px-2 py-3 text-center disabled:cursor-not-allowed disabled:border-dashed disabled:bg-transparent disabled:text-muted ${choiceState(active)}`} data-id="element-8408" data-mp="2lyntwc098dfy" data-mpi={i}>
                  <span className="block text-[13px] capitalize" aria-hidden="true" data-id="element-8409" data-mp="2uwg1etzqigss">
                    {weekdayLong(d)}
                  </span>
                  <span className="mt-0.5 block font-display text-lg" aria-hidden="true" data-id="element-8410" data-mp="1jjnbloqi82fd">
                    {shortDate(d)}
                  </span>
                  {full ? <span className="mt-0.5 block text-[12px]" aria-hidden="true" data-id="element-8411" data-mp="2486cq9h2vwir">
                      Vol
                    </span> : who && <span className="mt-0.5 block truncate text-[12px] opacity-75" aria-hidden="true" data-id="element-8412" data-mp="294wtz04mcy5r">
                        met {who}
                      </span>}
                </button>;
          })}
          </div>
          </>}
      </fieldset>

      <fieldset data-id="element-8413" data-mp="2rxhi649dif38">
        <legend className={legend} data-id="element-8414" data-mp="2ryaj2olniadg">Tijdstip</legend>
        {/* Dag gekozen maar geen tijden (bijv. vandaag na sluitingstijd): dat zeggen, niet "Kies eerst een dag". */}
        {!b.day ? <p className="mt-3 text-[14px] text-muted" data-id="element-8415" data-mp="2i7fsj81dgom7">Kies eerst een dag.</p> : b.slots.length === 0 ? <p className="mt-3 text-[14px] text-muted" role="status" data-id="element-8416" data-mp="35xa2nhub3z2r">
            Op deze dag is er geen tijd meer vrij. Kies een andere dag.
          </p> :
      // Per dagdeel: op een drukke dag is een lange rij tijden op de telefoon slecht te scannen.
      <div className="mt-3 space-y-4" data-id="element-8417" data-mp="ua6lyblenedl">
            {dayParts(b.slots).map(part => <div key={part.label} role="group" aria-labelledby={`slots-${part.label}`} data-id="element-8418" data-mp="18z33r0d9k5re">
                <p id={`slots-${part.label}`} className="mb-2 text-[13px] text-muted" data-id="element-8419" data-mp="190xfbo2nalit">
                  {part.label}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-8420" data-mp="g9m3jgjejkkm">
                  {part.slots.map(t => <button key={t} type="button" aria-pressed={b.time === t} onClick={() => pickTime(t)} className={chip(b.time === t)} data-id="element-8421" data-mp="bc454k30gnqv">
                      {t}
                    </button>)}
                </div>
              </div>)}
          </div>}
      </fieldset>

      {!account && <fieldset id={DETAILS_ID} className="scroll-mt-4 space-y-3" data-id="element-8422" data-mp="21m724mhuplu7">
          <legend className={legend} data-id="element-8423" data-mp="1wpgl9wy82m7q">Jouw gegevens</legend>
          {/* Zichtbare labels: placeholders verdwijnen zodra je typt of de browser invult. */}
          <div className="mt-3" data-id="element-8424" data-mp="1fa6hb2874tnp">
            <label htmlFor="booking-name" className="mb-1.5 block text-[14px] text-ink" data-id="element-8425" data-mp="1a8qxfdhr8t6w">
              Naam
            </label>
            <input id="booking-name" value={b.name} autoComplete="name" onChange={e => b.setName(e.target.value)} required className={input} data-id="element-8426" data-mp="15h93h9e5fsvf" />
          </div>
          <div data-id="element-8427" data-mp="vk4deyh6nd9c">
            <label htmlFor="booking-email" className="mb-1.5 block text-[14px] text-ink" data-id="element-8428" data-mp="vkmjemtnd5ug">
              E-mailadres
            </label>
            <input id="booking-email" type="email" inputMode="email" value={b.email} autoComplete="email" onChange={e => b.setEmail(e.target.value)} onBlur={() => b.setEmailTouched(true)} required aria-invalid={b.emailInvalid || undefined} aria-describedby={b.emailInvalid ? 'booking-email-error' : undefined} className={input} data-id="element-8429" data-mp="rhhiupt4ne8g" />
            {b.emailInvalid && <p id="booking-email-error" className="mt-1.5 text-[13px] text-clay" data-id="element-8430" data-mp="2oh8d854edp66">
                Dit e-mailadres lijkt niet te kloppen. Bijvoorbeeld naam@voorbeeld.nl.
              </p>}
          </div>
          {b.askSource && <div className="pt-3" data-id="element-8431" data-mp="2y5qbofp7fk6n">
              <SourceChips id="booking-source" value={b.source} onChange={b.setSource} legend={<>
                    {acquisitionQuestion} <span className="text-muted" data-id="element-8433" data-mp="2ahgtdnr79ek9">(hoeft niet)</span>
                  </>} legendClassName="text-[14px] text-ink" data-id="element-8432" data-mp="2y8w0dttx8k1u" />
            </div>}
          <p className="text-[13px] leading-relaxed text-muted" data-id="element-8434" data-mp="3lwm85q4lv3k8">
            Je aanvraag is vrijblijvend. {founder.firstName} bevestigt persoonlijk binnen {policy.responseHours} uur en stemt de behandeling vooraf met je af. Liever
            mailen?{' '}
            <a href={`mailto:${salon.email}`} className={linkCls} data-id="element-8435" data-mp="1qrwb8z2mt7rn">
              {salon.email}
            </a>
          </p>
        </fieldset>}
    </form>;
}